import { useState, type FormEvent } from "react";
import { QUESTION_TYPES, ApiRequestError, type ChapterDto, type QuestionType } from "@mmm/contracts";
import { ProcessingStatus } from "../molecules/ProcessingStatus.js";
import { Modal } from "../molecules/Modal.js";
import { QUESTION_TYPE_LABEL_KEYS } from "../../constants.js";
import { generateAITest } from "../../api/commands.js";
import { getCreditBalance, getCreditPricing } from "../../api/queries.js";
import { useQuery } from "../../api/useQuery.js";
import type { SubjectWithChildren } from "../../types.js";
import { DialogActions } from "../molecules/DialogActions.js";
import { useSettingsLauncher } from "../shell/SettingsLauncherContext.js";
import { Button, Card, Checkbox, Input, Pill, useT } from "@mmm/ui";
interface GenerateTestDialogProps {
  subject: SubjectWithChildren;
  /** When set, starts with this chapter selected; other sources remain available. */
  lockedChapter?: ChapterDto;
  onClose: () => void;
  onCreated: (test: { id: string }) => void;
}

export function GenerateTestDialog({ subject, lockedChapter, onClose, onCreated }: GenerateTestDialogProps) {
  const t = useT();
  const openSettings = useSettingsLauncher();
  const balance = useQuery(getCreditBalance, undefined);
  const pricing = useQuery(getCreditPricing, undefined);
  const [title, setTitle] = useState("");
  const [chapterIds, setChapterIds] = useState<string[]>(() => lockedChapter ? [lockedChapter.id] : subject.chapters.filter((chapter) => chapter.status === "active").map((chapter) => chapter.id));
  const [includeSubjectNotes, setIncludeSubjectNotes] = useState(!lockedChapter);
  const [questionCount, setQuestionCount] = useState(4);
  const [allowedTypes, setAllowedTypes] = useState<QuestionType[]>([...QUESTION_TYPES]);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const activeChapters = subject.chapters.filter((chapter) => chapter.status === "active");
  const charge = pricing.data ? questionCount * pricing.data.generationPerQuestion : null;
  const canAfford = charge !== null && balance.data && !balance.error && balance.data.available >= charge;
  const needsCredits = balance.data && !balance.error && (charge === null ? balance.data.available === 0 : balance.data.available < charge);

  const toggleType = (type: QuestionType) => {
    setAllowedTypes((prev) => (prev.includes(type) ? prev.filter((t) => t !== type) : [...prev, type]));
  };

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault();
    if (submitting || !canAfford) return;
    setSubmitting(true);
    setError(null);
    try {
      const result = await generateAITest({
        subjectId: subject.id,
        chapterIds,
        includeSubjectNotes,
        title: title.trim() || null,
        questionCount: Number(questionCount),
        allowedQuestionTypes: allowedTypes,
      });
      onCreated(result.test);
    } catch (err) {
      if (err instanceof ApiRequestError) setError(err.code === "insufficientCredits" ? t("credits.insufficient") : err.message);
      // Other errors (network unreachable, expired session, ...) are already
      // toasted by commands.ts's run() — the form's onSubmit invokes this
      // without awaiting it, so this must not rethrow.
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal onClose={submitting ? undefined : onClose} width={440} ariaLabel={t("tests.generateTest")}>
      <h2>{t("tests.generateTest")}</h2>
      <form onSubmit={handleSubmit}>
        <fieldset className="mmm-form-fields" disabled={submitting}>
        <label className="mmm-field-label" htmlFor="generate-test-title">
          {t("tests.titleOptional")}
        </label>
        <Input id="generate-test-title" value={title} onChange={(e) => setTitle(e.target.value)} />

        <fieldset className="mmm-form-fields mmm-test-scope-group">
          <legend className="mmm-field-label mmm-field-label--spaced">{t("tests.chapterScope")}</legend>
          <Card padding="sm" className="mmm-test-scope-picker">
            <Checkbox label={t("tests.includeSubjectNotes")} checked={includeSubjectNotes} onChange={(event) => setIncludeSubjectNotes(event.target.checked)} />
            {activeChapters.map((chapter) => (
              <Checkbox key={chapter.id} label={chapter.title} checked={chapterIds.includes(chapter.id)} onChange={(event) => setChapterIds((current) => event.target.checked ? [...current, chapter.id] : current.filter((id) => id !== chapter.id))} />
            ))}
          </Card>
        </fieldset>

        <label className="mmm-field-label mmm-field-label--spaced" htmlFor="generate-test-question-count">
          {t("tests.questionCount")}
        </label>
        <Input
          id="generate-test-question-count"
          type="number"
          min="1"
          max="20"
          required
          value={questionCount}
          onChange={(e) => setQuestionCount(Number(e.target.value))}
        />

        <label className="mmm-field-label mmm-field-label--spaced">
          {t("tests.allowedQuestionTypes")}
        </label>
        <div className="mmm-token-button-row">
          {QUESTION_TYPES.map((type) => (
            <Pill
              key={type}
              active={allowedTypes.includes(type)}
              color="var(--mmm-purple)"
              onClick={() => toggleType(type)}
            >
              {t(QUESTION_TYPE_LABEL_KEYS[type])}
            </Pill>
          ))}
        </div>

        </fieldset>
        <Card padding="sm">
          <p>{charge === null ? t("credits.unavailable") : t("tests.generationCharge", { count: charge })}</p>
          <p>{t("tests.firstMarkIncluded")}</p>
          {needsCredits && <p>{t("credits.insufficientForGeneration")}</p>}
          {needsCredits && <Button type="button" variant="secondary" onClick={() => { onClose(); openSettings("earn"); }}>{t("settings.section.earn")}</Button>}
        </Card>
        {submitting && <ProcessingStatus title={t("tests.generatingTest")} message={t("tests.generationProgress")} />}
        {error && <p className="mmm-form-error">{error}</p>}

        <DialogActions onCancel={onClose} confirmLabel={charge === null ? t("tests.generateTest") : t("tests.generateForCredits", { count: charge })} confirmVariant="primary" confirmDisabled={!canAfford || allowedTypes.length === 0 || (!includeSubjectNotes && chapterIds.length === 0)} submitting={submitting} />
      </form>
    </Modal>
  );
}
